/**
 * Temporary migration telemetry: counts every auth-related call to
 * database-api so we can verify they reach zero after the Firebase-only
 * auth migration. Fire-and-forget; delete along with the legacy auth paths.
 *
 * Lives in its own module (not next-auth/config.ts) so the browser-side
 * axios interceptor can import it without pulling server-only code into
 * the client bundle.
 */
function capturePosthogEvent(
  posthog: { apiKey?: string; host?: string } | undefined,
  event: string,
  distinctId: string,
  properties: Record<string, unknown>,
) {
  try {
    if (!posthog?.apiKey) return;
    const host = posthog.host || "https://eu.i.posthog.com";
    fetch(`${host}/capture/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        api_key: posthog.apiKey,
        event,
        distinct_id: distinctId,
        properties,
      }),
      // Survives the page unload/redirect that follows browser-side sign-outs.
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Fire-and-forget — telemetry must not affect auth flow
  }
}

export function trackDbApiAuthCall(
  posthog: { apiKey?: string; host?: string } | undefined,
  info: {
    endpoint: "auth_refresh" | "auth_provider";
    caller: "jwt_signin" | "jwt_legacy_refresh" | "browser_401_fallback";
    app?: string;
    userId?: string;
  },
) {
  capturePosthogEvent(
    posthog,
    "db_api_auth_request",
    info.userId || "unknown",
    {
      endpoint: info.endpoint,
      caller: info.caller,
      app: info.app,
    },
  );
}

export function trackLegacySessionUpgrade(
  posthog: { apiKey?: string; host?: string } | undefined,
  info: {
    event:
      | "auth_legacy_session_upgrade_attempted"
      | "auth_legacy_session_upgrade_unavailable"
      | "auth_legacy_session_upgraded"
      | "auth_legacy_session_upgrade_failed"
      | "auth_legacy_session_upgrade_skipped";
    app?: string;
    email?: string | null;
    userId?: string;
    firebaseUserPresent?: boolean;
    featureFlagEnabled?: boolean;
    reason?: string;
  },
) {
  capturePosthogEvent(posthog, info.event, info.userId || "unknown", {
    app: info.app,
    email: info.email || undefined,
    firebase_user_present: info.firebaseUserPresent,
    feature_flag_enabled: info.featureFlagEnabled,
    reason: info.reason,
    user_id: info.userId,
  });
}
